import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import { money } from "@/lib/format";
import type { Product } from "@/types";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { QueryError } from "@/components/shared/QueryError";
import { CustomFieldsEditor } from "@/components/custom-fields/CustomFieldsEditor";
import { DeleteButton } from "@/components/shared/DeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const schema = z.object({
  sku: z.string().trim().min(1, "Référence requise").max(40).regex(/^[A-Z0-9_-]+$/i, "Lettres, chiffres, - et _ uniquement"),
  name: z.string().trim().min(1, "Nom requis").max(120),
  sector: z.string().trim().max(80).optional().or(z.literal("")),
  lead_type: z.string().trim().max(80).optional().or(z.literal("")),
  description: z.string().max(2000).optional().or(z.literal("")),
  unit_price_eur: z.coerce.number().min(0).nullable(),
  unit_price_usd: z.coerce.number().min(0).nullable(),
  is_active: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

function useProducts() {
  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const { data, error } = await supabase.from("products").select("*").order("position").order("name");
      if (error) throw error;
      return data;
    },
  });
}

function ProductForm({ product, onDone }: { product?: Product; onDone: () => void }) {
  const qc = useQueryClient();
  const [custom, setCustom] = useState<Record<string, unknown>>((product?.custom_fields as Record<string, unknown>) ?? {});
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      sku: product?.sku ?? "", name: product?.name ?? "", sector: product?.sector ?? "", lead_type: product?.lead_type ?? "",
      description: product?.description ?? "",
      unit_price_eur: product?.unit_price_eur === null || product?.unit_price_eur === undefined ? null : Number(product.unit_price_eur),
      unit_price_usd: product?.unit_price_usd === null || product?.unit_price_usd === undefined ? null : Number(product.unit_price_usd),
      is_active: product?.is_active ?? true,
    },
  });

  const save = useMutation({
    mutationFn: async (v: FormValues) => {
      const payload = {
        sku: v.sku.toUpperCase(), name: v.name, sector: v.sector || null, lead_type: v.lead_type || null,
        description: v.description || null, unit_price_eur: v.unit_price_eur, unit_price_usd: v.unit_price_usd,
        is_active: v.is_active, custom_fields: custom as never,
      };
      const q = product
        ? supabase.from("products").update(payload).eq("id", product.id)
        : supabase.from("products").insert(payload);
      const { error } = await q;
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      toast.success(product ? "Produit mis à jour" : "Produit créé");
      logger.info(product ? "product.updated" : "product.created", { sku: form.getValues("sku") });
      onDone();
    },
    onError: (e) => {
      const msg = (e as { code?: string }).code === "23505" ? "Cette référence existe déjà." : "Enregistrement impossible.";
      toast.error(msg); logger.error("product.save", e);
    },
  });

  const err = form.formState.errors;
  return (
    <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="sku">Référence *</Label>
          <Input id="sku" placeholder="LEAD-PLOMB-FR" {...form.register("sku")} />
          {err.sku && <p className="text-xs text-destructive">{err.sku.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="name">Nom *</Label>
          <Input id="name" placeholder="Lead plomberie qualifié" {...form.register("name")} />
          {err.name && <p className="text-xs text-destructive">{err.name.message}</p>}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sector">Secteur / métier</Label>
          <Input id="sector" placeholder="Plomberie" {...form.register("sector")} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lead_type">Type de lead</Label>
          <Input id="lead_type" placeholder="Formulaire, RDV, appel qualifié…" {...form.register("lead_type")} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="eur">Prix unitaire EUR</Label>
          <Input id="eur" type="number" step="0.01" min="0" {...form.register("unit_price_eur")} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="usd">Prix unitaire USD</Label>
          <Input id="usd" type="number" step="0.01" min="0" {...form.register("unit_price_usd")} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="description">Description</Label>
        <Textarea id="description" rows={3} {...form.register("description")} />
      </div>
      <CustomFieldsEditor entity="products" value={custom} onChange={setCustom} />
      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={form.watch("is_active")} onCheckedChange={(c) => form.setValue("is_active", c)} /> Actif au catalogue
        </label>
        <div className="flex gap-2">
          {product && <DeleteButton entity="product" id={product.id} variant="outline" onDeleted={onDone} onArchived={onDone} />}
          <Button type="submit" disabled={save.isPending}>{product ? "Enregistrer" : "Créer le produit"}</Button>
        </div>
      </div>
    </form>
  );
}

export default function CataloguePage() {
  const { data: products, isLoading, error } = useProducts();
  const [editing, setEditing] = useState<Product | null | "new">(null);

  return (
    <div>
      <PageHeader
        title="Catalogue"
        description="Chaque type de lead est un produit avec son prix unitaire par devise. Les remises se négocient par client ou par devis."
        actions={<Button onClick={() => setEditing("new")}><Plus className="mr-2 h-4 w-4" />Nouveau produit</Button>}
      />
      {error ? <QueryError error={error} /> : isLoading ? <p className="text-sm text-muted-foreground">Chargement…</p> : !products?.length ? (
        <EmptyState title="Aucun produit" description="Commence par définir tes types de leads et leurs prix." action={<Button onClick={() => setEditing("new")}>Créer le premier produit</Button>} />
      ) : (
        <div className="glass overflow-hidden rounded-2xl">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Référence</TableHead><TableHead>Nom</TableHead><TableHead>Secteur</TableHead><TableHead>Type</TableHead>
                <TableHead className="text-right">EUR</TableHead><TableHead className="text-right">USD</TableHead><TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} className="cursor-pointer" onClick={() => setEditing(p)}>
                  <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.sector ?? "—"}</TableCell>
                  <TableCell>{p.lead_type ?? "—"}</TableCell>
                  <TableCell className="text-right">{money(p.unit_price_eur, "EUR")}</TableCell>
                  <TableCell className="text-right">{money(p.unit_price_usd, "USD")}</TableCell>
                  <TableCell><Badge variant={p.is_active ? "default" : "secondary"}>{p.is_active ? "Actif" : "Inactif"}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing === "new" ? "Nouveau produit" : "Modifier le produit"}</DialogTitle></DialogHeader>
          {editing !== null && <ProductForm product={editing === "new" ? undefined : editing} onDone={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
